import { CampaignCampaignStatus } from "@/api/__generated__/models";
import { cn } from "@/lib/utils";
import { formatFullDateTimeRange } from "@/utils/date";
import { StatusBadge, Typography } from "@backpac/monica";
import { Temporal } from "temporal-polyfill";

interface CampaignCardProps {
  campaignTitle: string;
  thumbnail: string;
  materialTitle?: string;
  applyDates?: string;
  dateRange?: [Temporal.PlainDateTime, Temporal.PlainDateTime];
  status?: CampaignCampaignStatus;
  platform?: string;
  className?: string;
  onClick?: () => void;
}

export function CampaignCard({
  thumbnail,
  status,
  campaignTitle,
  materialTitle,
  dateRange,
  applyDates,
  platform,
  className,
}: CampaignCardProps) {
  const isEnded = status === CampaignCampaignStatus.CLOSED || status === CampaignCampaignStatus.EARLY_CLOSED;
  const isOngoing = status === CampaignCampaignStatus.SIGN_UP || status === CampaignCampaignStatus.UPCOMING;

  return (
    <div className={cn("flex flex-col gap-3 p-6 bg-white rounded-[12px] shadow-standard", className)}>
      <div className="relative w-full h-40 rounded-xl overflow-hidden space-y-3">
        <img src={thumbnail} alt={`Campaign thumbnail for ${campaignTitle}`} className="w-full h-full object-cover" />
        <div className="absolute bottom-2 left-2">
          {isEnded ? <StatusBadge theme="red">Ended</StatusBadge> : null}
          {isOngoing ? <StatusBadge theme="blue">Ongoing</StatusBadge> : null}
        </div>
      </div>
      <Typography size="sm" weight={600} className="line-clamp-2 text-ellipsis overflow-hidden h-10">
        {campaignTitle}
      </Typography>

      <div className="flex flex-col gap-2">
        {materialTitle != null ? (
          <div className="flex items-center gap-2 text-textMuted">
            <i className="i-bp-info inline-block w-4 h-4 flex-shrink-0" />
            <Typography size="xs" weight={500} color="textMuted">
              {materialTitle}
            </Typography>
          </div>
        ) : null}
        {applyDates != null ? (
          <div className="flex items-center gap-2 text-textMuted">
            <i className="i-bp-calendar inline-block w-4 h-4 flex-shrink-0" />
            <Typography size="xs" weight={500} color="textMuted">
              {applyDates}
            </Typography>
          </div>
        ) : null}
        {dateRange != null ? (
          <div className="flex items-center gap-2 text-textMuted">
            <i className="i-bp-calendar inline-block w-4 h-4 flex-shrink-0" />
            <Typography size="xs" weight={500} color="textMuted">
              {formatFullDateTimeRange([
                Temporal.PlainDateTime.from(dateRange[0]),
                Temporal.PlainDateTime.from(dateRange[1]),
              ])}{" "}
              (PDT)
            </Typography>
          </div>
        ) : null}
        {platform != null ? (
          <div className="flex items-center gap-2 text-textMuted">
            <i className="i-bp-video inline-block w-4 h-4 flex-shrink-0" />
            <Typography size="xs" weight={500} color="textMuted">
              {platform}
            </Typography>
          </div>
        ) : null}
      </div>
    </div>
  );
}

CampaignCard.StatusBadge = function CampaignStatusBadge({
  campaignStatus,
  className,
}: {
  campaignStatus: CampaignCampaignStatus;
  className?: string;
}) {
  const theme = {
    [CampaignCampaignStatus.UPCOMING]: "blue" as const,
    [CampaignCampaignStatus.SIGN_UP]: "blue" as const,
    [CampaignCampaignStatus.CLOSED]: "red" as const,
    [CampaignCampaignStatus.EARLY_CLOSED]: "red" as const,
  }[campaignStatus];
  const label = {
    [CampaignCampaignStatus.UPCOMING]: "Ongoing",
    [CampaignCampaignStatus.SIGN_UP]: "Ongoing",
    [CampaignCampaignStatus.CLOSED]: "Ended",
    [CampaignCampaignStatus.EARLY_CLOSED]: "Ended",
  }[campaignStatus];

  return (
    <StatusBadge theme={theme} className={className}>
      {label}
    </StatusBadge>
  );
};
